import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import WorkspacePage from "@/pages/WorkspacePage";
import AccountsPage from "@/pages/AccountsPage";
import AccountAnalyticsPage from "@/pages/AccountAnalyticsPage";
import PortfoliosPage from "@/pages/PortfoliosPage";
import JournalPage from "@/pages/JournalPage";
import SharedWorkspacePage from "@/pages/SharedWorkspacePage";
import SharedAccountPage from "@/pages/SharedAccountPage";
import SharedPortfolioPage from "@/pages/SharedPortfolioPage";
import AboutPage, { ContactPage } from "@/pages/AboutPage";
import AdminPage from "@/pages/AdminPage";
import { Loader2 } from "lucide-react";

function FullScreenLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/share/:token" element={<SharedWorkspacePage />} />
      <Route path="/shared/account/:token" element={<SharedAccountPage />} />
      <Route path="/shared/portfolio/:token" element={<SharedPortfolioPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/app/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <WorkspacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/accounts"
        element={
          <ProtectedRoute>
            <PortfoliosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/portfolio/:portfolioId"
        element={
          <ProtectedRoute>
            <AccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/account/:accountId"
        element={
          <ProtectedRoute>
            <AccountAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
